/**
 * WebSocket hook for real-time task updates.
 *
 * Contractors can ONLY subscribe to the 'my_tasks' channel.
 * This restriction is enforced both client-side (here) and server-side.
 *
 * Handles offline gracefully - skips connection when offline.
 */

import { useEffect, useRef, useCallback, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useOnlineStatus } from './useOfflineSync';

const API_BASE =
  import.meta.env.VITE_API_URL ??
  (import.meta.env.DEV ? 'http://localhost:8787' : '');

export interface WebSocketMessage {
  type: string;
  cursor?: string;
  taskId?: string;
  [key: string]: unknown;
}

export type ConnectionState =
  | 'connecting'
  | 'connected'
  | 'disconnected'
  | 'offline'
  | 'error'
  | 'unauthenticated';

interface UseWebSocketOptions {
  enabled?: boolean;
  onMessage?: (message: WebSocketMessage) => void;
  onTaskAssigned?: () => void;
}

/**
 * Contractor WebSocket hook - subscribes to 'my_tasks' channel only.
 *
 * Contractors are restricted to this single channel by both client and server.
 * Any attempt to subscribe to other channels (schedule, project:*) will be
 * rejected by the server with FORBIDDEN error.
 */
export function useWebSocket(options: UseWebSocketOptions = {}) {
  const { enabled = true, onMessage, onTaskAssigned } = options;
  const isOnline = useOnlineStatus();
  const wsRef = useRef<WebSocket | null>(null);
  const queryClient = useQueryClient();
  const [connectionState, setConnectionState] =
    useState<ConnectionState>('disconnected');
  const [lastMessage, setLastMessage] = useState<WebSocketMessage | null>(null);
  const [lastConnectedAt, setLastConnectedAt] = useState<Date | null>(null);
  const reconnectTimeoutRef = useRef<number>();
  const reconnectAttempts = useRef(0);
  const isConnectingRef = useRef(false);

  const connect = useCallback(async () => {
    // Skip connection if disabled or offline
    if (!enabled || !isOnline) {
      if (!isOnline) {
        setConnectionState('offline');
      }
      return;
    }

    // Guard against multiple connections (React strict mode, etc.)
    if (wsRef.current?.readyState === WebSocket.OPEN ||
        wsRef.current?.readyState === WebSocket.CONNECTING) {
      return;
    }

    // Guard against multiple simultaneous connection attempts during async ticket fetch
    if (isConnectingRef.current) return;
    isConnectingRef.current = true;

    try {
      setConnectionState('connecting');

      // Get WebSocket ticket (requires authentication)
      const ticketUrl = `${API_BASE}/api/v1/ws-ticket`;
      const headers: HeadersInit = {
        'Content-Type': 'application/json',
      };

      // Add mock auth header for development
      if (import.meta.env.DEV) {
        (headers as Record<string, string>)['X-Mock-User-Role'] = 'contractor';
      }

      const ticketRes = await fetch(ticketUrl, {
        method: 'POST',
        headers,
        credentials: 'include',
      });

      if (ticketRes.status === 401) {
        // Authentication failed - don't retry, user needs to re-login
        console.warn('[WebSocket] Authentication failed (401) - not retrying');
        setConnectionState('unauthenticated');
        isConnectingRef.current = false;
        return;
      }

      if (!ticketRes.ok) {
        throw new Error(`Failed to get WebSocket ticket: ${ticketRes.status}`);
      }

      const ticketJson = await ticketRes.json();
      const ticket = ticketJson.data?.ticket;
      if (!ticket) {
        throw new Error('Invalid WebSocket ticket response');
      }

      // Connect with ticket
      const wsProtocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
      let wsHost: string;
      if (API_BASE) {
        try {
          wsHost = new URL(API_BASE).host;
        } catch (err) {
          console.error('Invalid API_BASE for WebSocket:', API_BASE, err);
          wsHost = window.location.host;
        }
      } else {
        wsHost = window.location.host;
      }
      const wsUrl = `${wsProtocol}//${wsHost}/ws?ticket=${ticket}`;
      const ws = new WebSocket(wsUrl);

      ws.onopen = () => {
        setConnectionState('connected');
        setLastConnectedAt(new Date());
        reconnectAttempts.current = 0;

        // Contractors can ONLY subscribe to my_tasks channel
        // Server will reject any other channel with FORBIDDEN error
        ws.send(JSON.stringify({ type: 'subscribe', channel: 'my_tasks' }));
      };

      ws.onmessage = (event) => {
        try {
          const message = JSON.parse(event.data) as WebSocketMessage;
          setLastMessage(message);

          // Call custom message handler if provided
          onMessage?.(message);

          // Handle message types with query invalidation
          switch (message.type) {
            case 'my_tasks_updated':
              // Invalidate tasks query to refetch
              queryClient.invalidateQueries({ queryKey: ['tasks'] });

              // Notify about new assignment if handler provided
              onTaskAssigned?.();
              break;

            case 'ping':
              ws.send(JSON.stringify({ type: 'pong' }));
              break;

            case 'subscribed':
              // Successfully subscribed to my_tasks channel
              break;

            case 'error':
              console.error('WebSocket error message:', message);
              // If FORBIDDEN error, don't retry - this is a permission issue
              if (message.code === 'FORBIDDEN') {
                setConnectionState('error');
              }
              break;

            case 'cursor_expired':
              // Need to re-fetch full state
              queryClient.invalidateQueries({ queryKey: ['tasks'] });
              break;
          }
        } catch (err) {
          console.error('Failed to parse WebSocket message:', err);
        }
      };

      ws.onclose = (event) => {
        wsRef.current = null;

        // If we went offline, update state accordingly
        if (!navigator.onLine) {
          setConnectionState('offline');
          return;
        }

        setConnectionState('disconnected');

        // Don't reconnect if closed cleanly (code 1000) or if disabled
        if (event.code === 1000 || !enabled) return;

        // Exponential backoff reconnect with jitter
        // Max 10 attempts, max 30 second delay
        const delay = Math.min(
          1000 * Math.pow(2, reconnectAttempts.current),
          30000
        );
        const jitter = Math.random() * 1000;
        reconnectAttempts.current++;

        if (reconnectAttempts.current < 10) {
          reconnectTimeoutRef.current = window.setTimeout(
            connect,
            delay + jitter
          );
        } else {
          setConnectionState('error');
        }
      };

      ws.onerror = () => {
        setConnectionState('error');
        ws.close();
      };

      wsRef.current = ws;
    } catch (error) {
      console.error('[WebSocket] Connection failed:', error);
      setConnectionState('error');

      // Retry after delay if online (with exponential backoff)
      if (isOnline && reconnectAttempts.current < 10) {
        const delay = Math.min(
          1000 * Math.pow(2, reconnectAttempts.current),
          30000
        );
        const jitter = Math.random() * 1000;
        reconnectAttempts.current++;
        console.log(`[WebSocket] Scheduling retry ${reconnectAttempts.current}/10 in ${Math.round(delay + jitter)}ms`);
        reconnectTimeoutRef.current = window.setTimeout(connect, delay + jitter);
      } else if (reconnectAttempts.current >= 10) {
        console.warn('[WebSocket] Max retries reached, stopping');
      }
    } finally {
      isConnectingRef.current = false;
    }
  }, [enabled, isOnline, onMessage, onTaskAssigned, queryClient]);

  // Connect on mount and when online status changes
  useEffect(() => {
    if (enabled && isOnline) {
      connect();
    } else if (!isOnline) {
      // Close existing connection when going offline
      if (wsRef.current) {
        wsRef.current.close(1000, 'Offline');
      }
      setConnectionState('offline');
    }

    return () => {
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
      if (wsRef.current) {
        wsRef.current.close(1000, 'Component unmounted');
        wsRef.current = null;
      }
      isConnectingRef.current = false;
    };
  }, [connect, enabled, isOnline]);

  // Manual reconnect function
  const reconnect = useCallback(() => {
    if (!isOnline) {
      setConnectionState('offline');
      return;
    }

    if (wsRef.current) {
      wsRef.current.close(1000, 'Reconnecting');
    }
    reconnectAttempts.current = 0;
    connect();
  }, [connect, isOnline]);

  return {
    connectionState,
    connected: connectionState === 'connected',
    isOnline,
    lastMessage,
    lastConnectedAt,
    reconnect,
  };
}
